import { Link } from 'react-router-dom'
import SeoHead from '../components/SeoHead'
import { isPageSeoEnabled } from '../config/siteConfig'
import './About.css'

const About = () => {
  const seoEnabled = isPageSeoEnabled('about')
  const values = [
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
        </svg>
      ),
      title: 'Quality First',
      description: 'Uncompromising commitment to pharmaceutical quality in every product we manufacture.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"/>
        </svg>
      ),
      title: 'Patient Care',
      description: 'Every decision we make is guided by our commitment to patient safety and well-being.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
        </svg>
      ),
      title: 'Innovation',
      description: 'Continuous research and development to bring advanced pharmaceutical solutions.'
    },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"/>
        </svg>
      ),
      title: 'Integrity',
      description: 'Maintaining highest ethical standards in all our business practices and relationships.'
    }
  ]

  const milestones = [
    { year: 'Foundation', description: 'SIBR Life Sciences established with a vision to provide quality healthcare products.' },
    { year: 'GMP Certified', description: 'Achieved GMP certification, marking our commitment to manufacturing excellence.' },
    { year: 'Product Launch', description: 'Launched our first range of pharmaceutical products in the Indian market.' },
    { year: 'Expansion', description: 'Expanded product portfolio and distribution network across multiple states.' }
  ]

  return (
    <div className="about-page">
      <SeoHead
        title="About SIBR Lifescience | GMP Certified Pharmaceutical Company"
        description="Learn about SIBR Lifescience, a GMP-certified pharmaceutical company committed to manufacturing quality healthcare products in India."
        canonical="/about"
        seoEnabled={seoEnabled}
      />
      
      {/* Hero */}
      <section className="page-hero about-hero">
        <div className="container">
          <span className="section-subtitle">About Us</span>
          <h1>Building Trust Through Quality Healthcare</h1>
          <p>
            SIBR Lifescience is a pharmaceutical company dedicated to developing and 
            manufacturing high-quality healthcare products that improve patient outcomes.
          </p>
        </div>
      </section>

      {/* Company Overview */}
      <section className="company-overview section">
        <div className="container">
          <div className="overview-grid">
            <div className="overview-content">
              <span className="section-subtitle">Our Story</span>
              <h2>Committed to Healthcare Excellence</h2>
              <p>
                SIBR Life Sciences Private Limited was founded with a clear mission: to make 
                quality healthcare accessible to all. Our state-of-the-art manufacturing 
                facility operates under strict GMP guidelines, ensuring every product meets 
                the highest standards of quality and efficacy.
              </p>
              <p>
                We specialize in developing pharmaceutical formulations that address various 
                healthcare needs, from nutritional supplements to prescription medications. 
                Our team of experienced professionals works tirelessly to ensure that every 
                product leaving our facility is safe, effective, and of the highest quality.
              </p>
              <p>
                Today, our products are available through leading healthcare platforms like 
                <a href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382" target="_blank" rel="noopener noreferrer"> 1mg</a>, 
                making them accessible to patients across India.
              </p>
            </div>
            <div className="overview-image">
              <div className="overview-card">
                <img src="/assets/images/logo.png" alt="SIBR Lifescience" className="overview-logo" />
                <div className="overview-stats">
                  <div className="stat-item">
                    <span className="stat-value">GMP</span>
                    <span className="stat-label">Certified Facility</span>
                  </div>
                  <div className="stat-item">
                    <span className="stat-value">10+</span>
                    <span className="stat-label">Products</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="vision-mission section bg-light">
        <div className="container">
          <div className="vm-grid">
            <div className="vm-card">
              <div className="vm-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  <path d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                </svg>
              </div>
              <h3>Our Vision</h3>
              <p>
                To be a leading pharmaceutical company recognized for quality, innovation, 
                and our contribution to improving healthcare outcomes for patients across India.
              </p>
            </div>
            <div className="vm-card">
              <div className="vm-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"/>
                </svg>
              </div>
              <h3>Our Mission</h3>
              <p>
                To develop and manufacture safe, effective, and affordable pharmaceutical 
                products that meet international quality standards, while maintaining 
                unwavering commitment to patient safety.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="values-section section">
        <div className="container">
          <div className="section-title">
            <span className="section-subtitle">Our Values</span>
            <h2>Principles That Guide Us</h2>
            <p>The foundation of everything we do at SIBR Lifescience.</p>
          </div>
          <div className="values-grid">
            {values.map((value, index) => (
              <div key={index} className="value-card">
                <div className="value-icon">{value.icon}</div>
                <h3>{value.title}</h3>
                <p>{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company History */}
      <section className="history-section section bg-light">
        <div className="container">
          <div className="section-title">
            <span className="section-subtitle">Our Journey</span>
            <h2>Company Milestones</h2>
          </div>
          <div className="timeline">
            {milestones.map((milestone, index) => (
              <div key={index} className="timeline-item">
                <div className="timeline-marker">
                  <span>{index + 1}</span>
                </div>
                <div className="timeline-content">
                  <h4>{milestone.year}</h4>
                  <p>{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Manufacturing */}
      <section className="manufacturing-section section">
        <div className="container">
          <div className="manufacturing-grid">
            <div className="manufacturing-content">
              <span className="section-subtitle">Manufacturing Excellence</span>
              <h2>Quality Control Overview</h2>
              <p>
                Our manufacturing facility operates under strict Good Manufacturing Practice 
                (GMP) guidelines. Every product undergoes rigorous quality control testing 
                at multiple stages:
              </p>
              <ul className="qc-list">
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Raw Material Testing & Verification
                </li>
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  In-Process Quality Checks
                </li>
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Finished Product Analysis
                </li>
                <li>
                  <svg viewBox="0 0 20 20" fill="currentColor" width="16" height="16">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd"/>
                  </svg>
                  Stability Testing Programs
                </li>
              </ul>
              <Link to="/quality" className="btn btn-primary">
                Learn More About Quality
              </Link>
            </div>
            <div className="manufacturing-image">
              <div className="cert-badges">
                <div className="cert-badge">
                  <span className="cert-name">GMP</span>
                  <span className="cert-desc">Good Manufacturing Practice</span>
                </div>
                <div className="cert-badge">
                  <span className="cert-name">ISO 9001</span>
                  <span className="cert-desc">Quality Management</span>
                </div>
                <div className="cert-badge">
                  <span className="cert-name">WHO-GMP</span>
                  <span className="cert-desc">International Standards</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="about-cta section-sm bg-primary">
        <div className="container">
          <div className="about-cta-content">
            <h2>Partner With Us</h2>
            <p>
              Interested in our products or exploring partnership opportunities? 
              We'd love to hear from you.
            </p>
            <div className="about-cta-actions">
              <Link to="/contact" className="btn btn-white">
                Contact Us
              </Link>
              <a 
                href="https://www.1mg.com/marketer/sibr-life-sciences-private-limited-84382"
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-secondary"
                style={{ borderColor: 'white', color: 'white' }}
              >
                View Products on 1mg
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default About
